import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError, API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import TercerosMaestro from "@/components/TercerosMaestro";
import { ArrowLeft, FileArrowDown, MagnifyingGlass, Warning, Users, Buildings } from "@phosphor-icons/react";

const EUR = (n) => (typeof n === "number" ? n.toLocaleString("es-ES", { style: "currency", currency: "EUR" }) : "—");
const norm = (v) => String(v ?? "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[.\-\s/]/g, "");

export default function PortalDirectorio() {
    const { id } = useParams();
    const [cliente, setCliente] = useState(null);
    const [empresas, setEmpresas] = useState([]);
    const [selectedEmpresa, setSelectedEmpresa] = useState("");
    const [ejercicio, setEjercicio] = useState("");
    const [ejercicios, setEjercicios] = useState([]);
    const [data, setData] = useState({ clientes: [], proveedores: [] });
    const [tab, setTab] = useState("clientes");
    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("num_facturas");
    const [sortDir, setSortDir] = useState("desc");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    async function loadClienteEmpresas() {
        try {
            const [c, e, f] = await Promise.all([
                api.get(`/asesor/clientes/${id}`),
                api.get(`/asesor/clientes/${id}/empresas`),
                api.get(`/asesor/clientes/${id}/facturas`),
            ]);
            setCliente(c.data);
            const items = e.data.items || [];
            setEmpresas(items);
            if (!selectedEmpresa && items.length > 0) setSelectedEmpresa(items[0].id);
            setEjercicios(f.data.ejercicios || []);
        } catch (err) {
            setError(formatApiError(err, "Error al cargar cliente"));
        }
    }

    async function loadDirectorio() {
        if (!selectedEmpresa) return;
        setLoading(true); setError("");
        try {
            const qs = [`empresa_id=${selectedEmpresa}`, "only_confirmed=1"];
            if (ejercicio) qs.push(`ejercicio=${ejercicio}`);
            const { data } = await api.get(`/asesor/clientes/${id}/directorio?${qs.join("&")}`);
            setData(data);
        } catch (err) {
            setError(formatApiError(err, "Error al cargar directorio"));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { loadClienteEmpresas(); /* eslint-disable-next-line */ }, [id]);
    useEffect(() => { loadDirectorio(); /* eslint-disable-next-line */ }, [selectedEmpresa, ejercicio]);

    function toggleSort(f) {
        if (sortBy === f) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortBy(f); setSortDir("desc"); }
    }
    const arrow = (f) => sortBy === f ? (sortDir === "asc" ? " ↑" : " ↓") : "";

    const rows = useMemo(() => {
        const src = tab === "clientes" ? data.clientes : data.proveedores;
        let r = Array.isArray(src) ? [...src] : [];
        if (search) {
            const q = norm(search);
            r = r.filter((it) => {
                const hay = [it.nombre, it.cif, (it.nombres_variantes || []).join(" ")].map(norm).join("|");
                return hay.includes(q);
            });
        }
        const numeric = new Set(["num_facturas", "base_total", "iva_total", "total"]);
        r.sort((a, b) => {
            const va = a[sortBy]; const vb = b[sortBy];
            if (numeric.has(sortBy)) {
                const na = Number(va || 0); const nb = Number(vb || 0);
                return sortDir === "asc" ? na - nb : nb - na;
            }
            const sa = String(va || "").toLowerCase();
            const sb = String(vb || "").toLowerCase();
            if (sa < sb) return sortDir === "asc" ? -1 : 1;
            if (sa > sb) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
        return r;
    }, [data, tab, search, sortBy, sortDir]);

    async function downloadExcel() {
        if (!selectedEmpresa) return;
        try {
            const qs = [`empresa_id=${selectedEmpresa}`, "only_confirmed=1"];
            if (ejercicio) qs.push(`ejercicio=${ejercicio}`);
            const url = `${API}/asesor/clientes/${id}/directorio.xlsx?${qs.join("&")}`;
            const res = await fetch(url, { credentials: "include" });
            if (!res.ok) throw new Error(await res.text());
            const blob = await res.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `directorio.xlsx`;
            document.body.appendChild(a); a.click(); a.remove();
        } catch (e) {
            alert(formatApiError(e, "Error al descargar"));
        }
    }

    const totales = useMemo(() => {
        return rows.reduce((acc, r) => ({
            num_facturas: acc.num_facturas + (r.num_facturas || 0),
            base_total: acc.base_total + (r.base_total || 0),
            iva_total: acc.iva_total + (r.iva_total || 0),
            total: acc.total + (r.total || 0),
        }), { num_facturas: 0, base_total: 0, iva_total: 0, total: 0 });
    }, [rows]);

    return (
        <div className="min-h-screen bg-[var(--gf-bg)]">
            <Navbar />
            <div className="max-w-6xl mx-auto p-6 md:p-10">
                <Link to={`/portal-asesor/${id}`} className="inline-flex items-center gap-1 text-sm text-[var(--gf-text-secondary)] hover:text-[var(--gf-navy)] mb-4" data-testid="link-back">
                    <ArrowLeft size={14} /> Volver al cliente
                </Link>

                <div className="flex items-baseline justify-between flex-wrap gap-3 mb-4">
                    <div>
                        <div className="text-[10px] tracking-widest uppercase text-[var(--gf-text-muted)]">Portal del Asesor</div>
                        <h1 className="serif text-3xl md:text-4xl text-[var(--gf-navy)]">Directorio · {cliente?.nombre || "…"}</h1>
                        <p className="text-sm text-[var(--gf-text-secondary)]">Maestro de clientes (facturas emitidas) y proveedores/acreedores (facturas recibidas). Solo se cuentan facturas <b>confirmadas</b> por ti para máxima fiabilidad.</p>
                    </div>
                </div>

                {/* Filtros globales */}
                <div className="gf-card mb-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <div>
                            <label className="gf-label">Empresa</label>
                            <select value={selectedEmpresa} onChange={(e) => setSelectedEmpresa(e.target.value)} className="gf-input text-sm py-1" data-testid="select-empresa">
                                {empresas.length === 0 && <option value="">Sin empresas</option>}
                                {empresas.map((em) => <option key={em.id} value={em.id}>{em.nombre}{em.cif ? ` · ${em.cif}` : ""}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="gf-label">Ejercicio</label>
                            <select value={ejercicio} onChange={(e) => setEjercicio(e.target.value)} className="gf-input text-sm py-1" data-testid="select-ejercicio">
                                <option value="">Todos</option>
                                {ejercicios.map((e) => <option key={e} value={e}>{e}</option>)}
                            </select>
                        </div>
                        <div className="flex-1 min-w-[240px]">
                            <label className="gf-label">Buscar</label>
                            <div className="relative">
                                <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--gf-text-muted)]" />
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="gf-input text-sm py-1 pl-7"
                                    placeholder="Nombre, CIF, variante…"
                                    data-testid="input-search"
                                />
                            </div>
                        </div>
                        <button onClick={downloadExcel} className="btn-outline text-sm inline-flex items-center gap-1 self-end" data-testid="btn-download-excel">
                            <FileArrowDown size={14} /> Descargar Excel
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 border-b border-[var(--gf-border)] mb-4">
                    <button
                        onClick={() => setTab("clientes")}
                        className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px inline-flex items-center gap-2 ${tab === "clientes" ? "border-[var(--gf-gold)] text-[var(--gf-navy)]" : "border-transparent text-[var(--gf-text-muted)] hover:text-[var(--gf-navy)]"}`}
                        data-testid="tab-clientes"
                    >
                        <Users size={16} weight={tab === "clientes" ? "fill" : "regular"} />
                        Clientes ({data.clientes?.length || 0})
                    </button>
                    <button
                        onClick={() => setTab("proveedores")}
                        className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px inline-flex items-center gap-2 ${tab === "proveedores" ? "border-[var(--gf-gold)] text-[var(--gf-navy)]" : "border-transparent text-[var(--gf-text-muted)] hover:text-[var(--gf-navy)]"}`}
                        data-testid="tab-proveedores"
                    >
                        <Buildings size={16} weight={tab === "proveedores" ? "fill" : "regular"} />
                        Proveedores / Acreedores ({data.proveedores?.length || 0})
                    </button>
                </div>

                {error && <div className="bg-red-50 border border-red-200 text-red-800 text-sm p-3 mb-4" data-testid="error-banner">{error}</div>}

                <TercerosMaestro clienteId={id} onFacturasChanged={loadDirectorio} />

                {loading ? (
                    <div className="gf-card text-center py-10 text-[var(--gf-text-secondary)]">Cargando…</div>
                ) : rows.length === 0 ? (
                    <div className="gf-card text-center py-10 text-[var(--gf-text-secondary)]" data-testid="empty">
                        {search ? `Sin resultados para "${search}".` :
                        (tab === "clientes"
                            ? "Aún no hay clientes en esta empresa. Confirma facturas emitidas para verlos aquí."
                            : "Aún no hay proveedores en esta empresa. Confirma facturas recibidas para verlos aquí.")}
                    </div>
                ) : (
                    <div className="gf-card p-0 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="data-table w-full" data-testid="tabla-directorio">
                                <thead>
                                    <tr>
                                        <th onClick={() => toggleSort("nombre")} className="cursor-pointer select-none hover:bg-[var(--gf-cream)]" data-testid="sort-nombre">Nombre{arrow("nombre")}</th>
                                        <th onClick={() => toggleSort("cif")} className="cursor-pointer select-none hover:bg-[var(--gf-cream)]" data-testid="sort-cif">CIF/NIF{arrow("cif")}</th>
                                        <th onClick={() => toggleSort("num_facturas")} className="cursor-pointer select-none hover:bg-[var(--gf-cream)] text-right" data-testid="sort-num">Nº fact.{arrow("num_facturas")}</th>
                                        <th onClick={() => toggleSort("base_total")} className="cursor-pointer select-none hover:bg-[var(--gf-cream)] text-right" data-testid="sort-base">Base total{arrow("base_total")}</th>
                                        <th onClick={() => toggleSort("iva_total")} className="cursor-pointer select-none hover:bg-[var(--gf-cream)] text-right">IVA total{arrow("iva_total")}</th>
                                        <th onClick={() => toggleSort("total")} className="cursor-pointer select-none hover:bg-[var(--gf-cream)] text-right" data-testid="sort-total">Importe total{arrow("total")}</th>
                                        <th onClick={() => toggleSort("primera_fecha")} className="cursor-pointer select-none hover:bg-[var(--gf-cream)]">1ª fecha{arrow("primera_fecha")}</th>
                                        <th onClick={() => toggleSort("ultima_fecha")} className="cursor-pointer select-none hover:bg-[var(--gf-cream)]">Última{arrow("ultima_fecha")}</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => (
                                        <tr key={r.key} data-testid={`row-${r.key}`}>
                                            <td>
                                                <div className="font-semibold text-[var(--gf-navy)]">{r.nombre || "(sin nombre)"}</div>
                                                {r.tiene_variantes && (
                                                    <div
                                                        className="text-[11px] text-amber-700 inline-flex items-center gap-1 cursor-help"
                                                        title={`Variantes de nombre encontradas:\n${(r.nombres_variantes || []).join("\n")}`}
                                                        data-testid={`variants-${r.key}`}
                                                    >
                                                        <Warning size={11} weight="fill" /> {r.nombres_variantes.length} variantes de nombre
                                                    </div>
                                                )}
                                                {r.sin_cif && (
                                                    <div className="text-[11px] text-rose-700 inline-flex items-center gap-1" title="Este contacto no tiene CIF detectado — se agrupa por nombre normalizado">
                                                        <Warning size={11} weight="fill" /> sin CIF (agrupado por nombre)
                                                    </div>
                                                )}
                                            </td>
                                            <td className="mono text-xs">{r.cif || "—"}</td>
                                            <td className="text-right mono">{r.num_facturas}</td>
                                            <td className="text-right mono">{EUR(r.base_total)}</td>
                                            <td className="text-right mono">{EUR(r.iva_total)}</td>
                                            <td className="text-right mono font-semibold">{EUR(r.total)}</td>
                                            <td className="mono text-xs">{r.primera_fecha || "—"}</td>
                                            <td className="mono text-xs">{r.ultima_fecha || "—"}</td>
                                            <td className="text-right">
                                                <Link
                                                    to={`/portal-asesor/${id}?empresa_id=${selectedEmpresa}&contraparte=${encodeURIComponent(`${r.cif || ""}|${r.nombre || ""}`)}`}
                                                    className="text-xs text-[var(--gf-navy)] hover:underline"
                                                    data-testid={`link-facturas-${r.key}`}
                                                    title="Ver todas las facturas de este contacto"
                                                >
                                                    Ver facturas →
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-[var(--gf-cream)] font-bold">
                                        <td colSpan={2}>TOTAL ({rows.length} {tab === "clientes" ? "clientes" : "proveedores"})</td>
                                        <td className="text-right mono">{totales.num_facturas}</td>
                                        <td className="text-right mono">{EUR(totales.base_total)}</td>
                                        <td className="text-right mono">{EUR(totales.iva_total)}</td>
                                        <td className="text-right mono">{EUR(totales.total)}</td>
                                        <td colSpan={3}></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
